/**
 * SSRoles — Roles & Permissions Admin (complete §6.6 coverage)
 *
 * Covers:
 *   Role CRUD
 *   Role → permissions (ssroles_permissions) — real GET /roles/{id}/permissions
 *   Role → users (GET /roles/{id}/users)
 *   User permission overrides (ssusers_permissions)
 *   Effective permissions viewer (ssvw_userspermissions)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssRoleAPI, ssUserAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, SplitLayout, TabBar, Section,
  Button, Badge, Field, Input, Select, Textarea,
  Spinner, EmptyState, ErrorBanner,
  DataTable, InfoGrid, SearchBar,
  SystemRoleBadge, PermissionEffectBadge, PermissionResultBadge,
  COLOR,
} from '../../components/ss';

// ── Create Role modal ─────────────────────────────────────────────────────────
function CreateRoleModal({ open, onClose, onDone }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]   = useState('');
  const upd = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => { if (open) { setForm({ name: '', description: '' }); setErr(''); } }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name) { setErr('Role name is required.'); return; }
    setSaving(true); setErr('');
    try { await ssRoleAPI.create({ name: form.name, description: form.description }); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title="Create Role" onClose={onClose} fullscreen>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14, marginBottom: 18 }}>
          <Field label="Role Name" required style={{ marginBottom: 0 }}>
            <Input required value={form.name} onChange={upd('name')} placeholder="e.g. CARE_COORDINATOR" />
          </Field>
          <Field label="Description" style={{ marginBottom: 0 }}>
            <Textarea value={form.description} onChange={upd('description')} placeholder="What can this role do?" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create Role'}</Button>
        </div>
      </form>
      </div>
    </Modal>
  );
}

// ── Assign User modal ─────────────────────────────────────────────────────────
function AssignUserModal({ role, open, onClose, onDone }) {
  const [users, setUsers]   = useState([]);
  const [userId, setUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    if (!open) return;
    ssUserAPI.list().then(d => setUsers(d.data || d || [])).catch(() => {});
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (!userId) { setErr('Select a user.'); return; }
    setSaving(true); setErr('');
    try { await ssRoleAPI.assignUser(userId, { role_id: role?.roleid }); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={`Assign User → ${role?.name}`} onClose={onClose}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <Field label="User" required style={{ marginBottom: 18 }}>
          <Select value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">Select user…</option>
            {users.map(u => <option key={u.userid} value={u.userid}>{u.username} — {u.email}</option>)}
          </Select>
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Assigning…' : 'Assign'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Grant Permission modal ────────────────────────────────────────────────────
function GrantPermModal({ role, open, onClose, onDone, forUser }) {
  const title = forUser ? `Grant Override → ${forUser?.username}` : `Grant Permission → ${role?.name}`;
  const [form, setForm] = useState({ module: '', object: '', method: '', allow: true, entity_id: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]   = useState('');
  const upd = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const updBool = k => e => setForm(p => ({ ...p, [k]: e.target.value === 'true' }));

  async function submit(e) {
    e.preventDefault();
    if (!form.module || !form.object || !form.method) { setErr('Module, object and method are required.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { module: form.module, object: form.object, method: form.method, allow: form.allow, entity_id: form.entity_id || null };
      if (forUser) await ssRoleAPI.grantUserPermOverride(forUser.userid, payload);
      else          await ssRoleAPI.grantPerm(role?.roleid, payload);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Module" required style={{ marginBottom: 14 }}><Input required value={form.module} onChange={upd('module')} placeholder="e.g. parties" /></Field>
          <Field label="Object" required style={{ marginBottom: 14 }}><Input required value={form.object} onChange={upd('object')} placeholder="e.g. Party" /></Field>
          <Field label="Method" required style={{ marginBottom: 14 }}><Input required value={form.method} onChange={upd('method')} placeholder="e.g. Create" /></Field>
          <Field label="Effect" style={{ marginBottom: 14 }}>
            <Select value={String(form.allow)} onChange={updBool('allow')}>
              <option value="true">✅ Allow</option>
              <option value="false">🚫 Deny</option>
            </Select>
          </Field>
          <Field label="Entity ID (row-level, optional)" style={{ gridColumn: '1/-1', marginBottom: 18 }}>
            <Input value={form.entity_id} onChange={upd('entity_id')} placeholder="Leave blank for global" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="teal" type="submit" disabled={saving}>{saving ? 'Granting…' : 'Grant Permission'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Role Detail panel — permissions, users, user overrides
// ─────────────────────────────────────────────────────────────────────────────
function RoleDetail({ role, onClose, onRefresh }) {
  const [tab, setTab]         = useState('perms');
  const [perms, setPerms]     = useState([]);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [showGrant, setShowGrant]   = useState(false);
  const [saving, setSaving]   = useState(false);

  const loadData = useCallback(async () => {
    if (!role) return;
    setLoading(true); setErr('');
    try {
      if (tab === 'perms') {
        const d = await ssRoleAPI.listRolePerms(role.roleid);
        setPerms(d.data || d || []);
      }
      if (tab === 'users') {
        const d = await ssRoleAPI.listRoleUsers(role.roleid);
        setUsers(d.data || d || []);
      }
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Load failed'); }
    finally { setLoading(false); }
  }, [role, tab]);

  useEffect(() => { setTab('perms'); }, [role]);
  useEffect(() => { loadData(); }, [loadData]);

  if (!role) return <EmptyState icon="🛡️" title="Select a role" message="Click a role on the left to manage it." />;

  const wrap = async (fn) => {
    setSaving(true); setErr('');
    try { await fn(); await loadData(); onRefresh(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const PERM_COLS = [
    {
      key: '_triplet', label: 'Module.Object.Method',
      render: (_, p) => (
        <div>
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {p.module_name || p.securitymodule}.{p.object_name || p.securityobject}.{p.method_name || p.securityobjectmethod || p.methodname}
          </span>
          {(p.entity_id || p.entityid) && <Badge color="gray" style={{ marginLeft: 6, fontSize: 10 }}>{p.entity_id || p.entityid}</Badge>}
        </div>
      ),
    },
    { key: 'isallow', label: 'Effect', render: (v, r) => <PermissionEffectBadge isAllow={v ?? r.allow} /> },
    {
      key: '_del', label: '',
      render: (_, p) => !role.internalofsystem && (
        <Button variant="danger" size="sm" onClick={() => wrap(() => ssRoleAPI.revokePerm(role.roleid, p.rolepermissionid || p.id || p.permissionid))}>Revoke</Button>
      ),
    },
  ];

  const USER_COLS = [
    { key: 'username', label: 'Username', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'email',    label: 'Email' },
    { key: '_del',     label: '',
      render: (_, u) => (
        <Button variant="danger" size="sm" onClick={() => wrap(() => ssRoleAPI.revokeUser(u.userid, role.roleid))}>Remove</Button>
      ),
    },
  ];

  const TABS = [
    { id: 'perms', label: `Permissions (${perms.length})` },
    { id: 'users', label: `Users (${users.length})` },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: COLOR.text }}>{role.name}</div>
          <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>{role.description || 'No description'}</div>
          <div style={{ marginTop: 6 }}><SystemRoleBadge isSystem={role.internalofsystem} /></div>
        </div>
        <Button variant="ghost" onClick={onClose}>×</Button>
      </div>

      <ErrorBanner>{err}</ErrorBanner>
      <TabBar tabs={TABS} active={tab} onChange={setTab} style={{ marginBottom: 16 }} />

      {loading && <Spinner />}

      {!loading && tab === 'perms' && (
        <Section action={!role.internalofsystem && <Button variant="teal" size="sm" onClick={() => setShowGrant(true)}>+ Grant Permission</Button>}>
          <DataTable columns={PERM_COLS} rows={perms} rowKey={r => r.rolepermissionid || r.id || Math.random()} emptyIcon="🔑" emptyTitle="No permissions defined" emptyMsg="Use Grant Permission to add rules." />
        </Section>
      )}

      {!loading && tab === 'users' && (
        <Section action={<Button size="sm" onClick={() => setShowAssign(true)}>+ Assign User</Button>}>
          <DataTable columns={USER_COLS} rows={users} rowKey="userid" emptyIcon="👤" emptyTitle="No users with this role" />
        </Section>
      )}

      <AssignUserModal role={role} open={showAssign} onClose={() => setShowAssign(false)} onDone={() => { setShowAssign(false); loadData(); onRefresh(); }} />
      <GrantPermModal role={role} open={showGrant} onClose={() => setShowGrant(false)} onDone={() => { setShowGrant(false); loadData(); }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// User Overrides panel — grant/revoke per-user permission overrides
// ─────────────────────────────────────────────────────────────────────────────
function UserOverridesPanel() {
  const [users, setUsers]       = useState([]);
  const [userId, setUserId]     = useState('');
  const [overrides, setOverrides] = useState([]);
  const [effPerms, setEffPerms]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [tab, setTab]           = useState('overrides');
  const [showGrant, setShowGrant] = useState(false);
  const [err, setErr]           = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    ssUserAPI.list().then(d => setUsers(d.data || d || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true); setErr('');
    try {
      const [ov, ef] = await Promise.all([
        ssRoleAPI.listUserPermsOverrides(userId).then(d => d.data || d || []),
        ssRoleAPI.effectivePerms(userId).then(d => d.data || d || []),
      ]);
      setOverrides(ov); setEffPerms(ef);
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { if (userId) load(); else { setOverrides([]); setEffPerms([]); } }, [userId, load]);

  const selectedUser = users.find(u => String(u.userid) === String(userId));

  const OV_COLS = [
    { key: '_triplet', label: 'Module.Object.Method',
      render: (_, p) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.module_name || p.securitymodule}.{p.object_name || p.securityobject}.{p.method_name || p.securityobjectmethod || p.methodname}</span> },
    { key: 'isallow', label: 'Effect', render: (v, r) => <PermissionEffectBadge isAllow={v ?? r.allow} /> },
    { key: '_del',    label: '', render: (_, p) => (
      <Button variant="danger" size="sm" onClick={async () => {
        setSaving(true);
        try { await ssRoleAPI.revokeUserPermOverride(userId, p.userpermissionid || p.id || p.permissionid); load(); }
        catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
        finally { setSaving(false); }
      }}>Revoke</Button>
    )},
  ];

  const EFF_COLS = [
    { key: 'module',    label: 'Module',  render: v => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span> },
    { key: 'object',    label: 'Object',  render: v => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span> },
    { key: 'method',    label: 'Method',  render: v => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span> },
    { key: 'entity_id', label: 'Entity',  render: v => <span style={{ color: COLOR.grayLight }}>{v || '*'}</span> },
    { key: 'isgranted', label: 'Granted', render: v => <PermissionResultBadge isGranted={v} /> },
  ];

  const TABS = [
    { id: 'overrides', label: `Overrides (${overrides.length})` },
    { id: 'effective', label: `Effective (${effPerms.length})` },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Select User" style={{ flex: '1 1 260px', marginBottom: 0 }}>
          <Select value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">— pick a user —</option>
            {users.map(u => <option key={u.userid} value={u.userid}>{u.username} ({u.email})</option>)}
          </Select>
        </Field>
        {userId && <Button size="sm" variant="teal" onClick={() => setShowGrant(true)}>+ Grant Override</Button>}
      </div>

      <ErrorBanner>{err}</ErrorBanner>

      {userId && (
        <>
          <TabBar tabs={TABS} active={tab} onChange={setTab} style={{ marginBottom: 16 }} />
          {loading ? <Spinner /> : (
            tab === 'overrides'
              ? <DataTable columns={OV_COLS} rows={overrides} rowKey={r => r.userpermissionid || r.id || Math.random()} emptyIcon="🔑" emptyTitle="No overrides" emptyMsg="User inherits all access from their roles." />
              : <DataTable columns={EFF_COLS} rows={effPerms} rowKey={(_, i) => i} emptyIcon="🔍" emptyTitle="No effective permissions" />
          )}
        </>
      )}

      <GrantPermModal forUser={selectedUser} open={showGrant} onClose={() => setShowGrant(false)} onDone={() => { setShowGrant(false); load(); }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────
export default function SSRoles() {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [mainTab, setMainTab] = useState('roles');

  const load = useCallback(() => {
    setLoading(true);
    ssRoleAPI.list()
      .then(d => { setRoles(d.data || d || []); setErr(''); })
      .catch(ex => setErr(ex.response?.data?.detail || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function deleteRole(r) {
    if (r.internalofsystem) { setErr('Cannot delete a system role.'); return; }
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await ssRoleAPI.delete(r.roleid); load(); if (selected?.roleid === r.roleid) setSelected(null); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  }

  const filtered = roles.filter(r => !search || r.name?.toLowerCase().includes(search.toLowerCase()));

  const PAGE_TABS = [
    { id: 'roles',     label: 'Roles & Permissions' },
    { id: 'overrides', label: 'User Overrides' },
  ];

  return (
    <Page>
      <PageHeader
        title="Roles & Permissions"
        subtitle="Define authorization roles, permission grants and per-user overrides (§6.6)"
        action={mainTab === 'roles' && <Button onClick={() => setShowCreate(true)}>+ Create Role</Button>}
      />

      <TabBar tabs={PAGE_TABS} active={mainTab} onChange={t => { setMainTab(t); setSelected(null); }} style={{ marginBottom: 20 }} />

      <ErrorBanner>{err}</ErrorBanner>

      {mainTab === 'roles' && (
        <SplitLayout
          listWidth={320}
          list={
            <Card>
              <SearchBar value={search} onChange={setSearch} placeholder="Search roles…" style={{ marginBottom: 12 }} />
              {loading ? <Spinner /> : (
                filtered.length === 0
                  ? <EmptyState icon="🛡️" title="No roles found" />
                  : filtered.map(r => (
                    <div key={r.roleid}
                      onClick={() => setSelected(r)}
                      style={{
                        padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                        background: selected?.roleid === r.roleid ? COLOR.blueBg : COLOR.grayBg,
                        border: `1px solid ${selected?.roleid === r.roleid ? COLOR.blueBorder : 'transparent'}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: COLOR.text }}>{r.name}</div>
                        {r.description && <div style={{ fontSize: 11, color: COLOR.textMuted, marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</div>}
                        {r.internalofsystem && <Badge color="teal" style={{ marginTop: 4 }}>System</Badge>}
                      </div>
                      {!r.internalofsystem && (
                        <Button variant="danger" size="sm" style={{ padding: '4px 8px' }}
                          onClick={ev => { ev.stopPropagation(); deleteRole(r); }}>✕</Button>
                      )}
                    </div>
                  ))
              )}
            </Card>
          }
          detail={
            <Card style={{ minHeight: 400 }}>
              <RoleDetail role={selected} onClose={() => setSelected(null)} onRefresh={load} />
            </Card>
          }
        />
      )}

      {mainTab === 'overrides' && (
        <Card>
          <UserOverridesPanel />
        </Card>
      )}

      <CreateRoleModal open={showCreate} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />
    </Page>
  );
}
